import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { MultiPolygon } from 'geojson';

export const UNDO_LIMIT = 100;

export type CustomMultipolygonState = {
  isEditing?: boolean;
  boundaries?: MultiPolygon;
  mode?: 'draw_polygon' | 'simple_select' | 'polygon_edit' | 'direct_select';
  newBoundaries?: MultiPolygon;
  undoStack: MultiPolygon[];
  redoStack: MultiPolygon[];
};

export const initialState: CustomMultipolygonState = {
  isEditing: undefined,
  boundaries: undefined,
  mode: 'draw_polygon',
  newBoundaries: undefined,
  undoStack: [],
  redoStack: []
};

const pushUndoEntry = (state: CustomMultipolygonState, boundaries: MultiPolygon) => {
  state.undoStack = [boundaries, ...state.undoStack];
  if (state.undoStack.length > UNDO_LIMIT) {
    state.undoStack = state.undoStack.slice(0, UNDO_LIMIT);
  }
  state.redoStack = [];
};

export const customMultipolygonSlice = createSlice({
  name: 'customMultipolygon',
  initialState,
  reducers: {
    resetState: () => initialState,
    setIsEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setMode: (state, action: PayloadAction<'draw_polygon' | 'simple_select' | 'polygon_edit' | 'direct_select'>) => {
      state.mode = action.payload;
    },
    setBoundaries: (state, action: PayloadAction<MultiPolygon | undefined>) => {
      state.boundaries = action.payload;
      if (action.payload) {
        pushUndoEntry(state, action.payload);
      } else {
        state.undoStack = [];
        state.redoStack = [];
      }
    },
    undo: state => {
      if (state.undoStack.length < 2) {
        return;
      }
      const current = state.undoStack[0];
      const previous = state.undoStack[1];
      state.boundaries = previous;
      // used to reset boundaries only after undo/redo.
      // the drawcontrols component is currently uncontrolled so we only control the component state when undo/redo is called.
      state.newBoundaries = previous;
      state.undoStack = state.undoStack.slice(1);
      state.redoStack = [current, ...state.redoStack];
    },
    redo: state => {
      if (state.redoStack.length < 1) {
        return;
      }
      const next = state.redoStack[0];
      state.boundaries = next;
      // used to reset boundaries only after undo/redo.
      // the drawcontrols component is currently uncontrolled so we only control the component state when undo/redo is called.
      state.newBoundaries = next;
      state.redoStack = state.redoStack.slice(1);
      state.undoStack = [next, ...state.undoStack];
    }
  }
});

export const { resetState, setIsEditing, setBoundaries, setMode, undo, redo } = customMultipolygonSlice.actions;

export const customMultipolygonReducer = customMultipolygonSlice.reducer;
